import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  collection,
  doc,
  updateDoc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { AppDispatch, RootState } from "./store";

/* ─────────────────────────────
   TYPES
───────────────────────────── */

export interface Notification {
  notificationId: string;
  userId: string;
  title: string;
  message: string;
  type?: string;
  read: boolean;
  createdAt?: number | null;
}

interface NotificationsState {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  unsubscribe?: () => void;
}

const initialState: NotificationsState = {
  notifications: [],
  loading: false,
  error: null,
  unsubscribe: undefined,
};

/* ─────────────────────────────
   FETCH ALL (ONE TIME)
───────────────────────────── */

export const fetchUserNotifications = createAsyncThunk<
  Notification[],
  string
>("notifications/fetchUserNotifications", async (userId) => {
  const q = query(
    collection(db, "notifications", userId, "messages"),
    orderBy("createdAt", "desc")
  );

  const snap = await getDocs(q);

  return snap.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      notificationId: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toMillis?.() || null,
    } as Notification;
  });
});

/* ─────────────────────────────
   REALTIME LISTENER
───────────────────────────── */
export const listenToUserNotifications =
  (userId: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    const existingUnsub = getState().notifications.unsubscribe;
    if (existingUnsub) {
      existingUnsub();
    }

    const q = query(
      collection(db, "notifications", userId, "messages"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
       

     

        const notifications: Notification[] = snap.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            notificationId: docSnap.id,
            ...data,
            createdAt: data.createdAt?.toMillis?.() || null,
          } as Notification;
        });

        dispatch(setNotifications(notifications));
      },
      (error) => {
        console.error("❌ FIRESTORE LISTENER ERROR:", error);
        dispatch(setError(error.message));
      }
    );

    dispatch(setUnsubscribe(unsubscribe));
    return unsubscribe;
  };

/* ─────────────────────────────
   MARK SINGLE AS READ
───────────────────────────── */

export const markNotificationAsRead = createAsyncThunk<
  string,
  { userId: string; notificationId: string }
>("notifications/markAsRead", async ({ userId, notificationId }) => {
  const messageRef = doc(
    db,
    "notifications",
    userId,
    "messages",
    notificationId
  );

  await updateDoc(messageRef, {
    read: true,
    updatedAt: serverTimestamp(),
  });

  return notificationId;
});

/* ─────────────────────────────
   MARK ALL AS READ
───────────────────────────── */
export const upsertNotification = createAsyncThunk<
  void,
  { userId: string; notificationId: string; data: Partial<Notification> }
>(
  "notifications/upsertNotification",
  async ({ userId, notificationId, data }, { rejectWithValue }) => {
    try {
      console.log("🔥 UPSERT START");
      console.log("👉 userId:", userId);
      console.log("👉 notificationId:", notificationId);
      console.log("👉 data:", data);

      const messagePath = `notifications/${userId}/messages/${notificationId}`;
      const rootPath = `notifications/${userId}`;

      console.log("📍 Subcollection Path:", messagePath);
      console.log("📍 Root Path:", rootPath);

      const messageRef = doc(
        db,
        "notifications",
        userId,
        "messages",
        notificationId
      );

      const rootRef = doc(db, "notifications", userId);

      // 🔥 1️⃣ UPSERT MESSAGE
      console.log("📝 Writing to subcollection...");
      await setDoc(
        messageRef,
        {
          ...data,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      console.log("✅ Subcollection upsert success");

      // 🔥 2️⃣ UPSERT ROOT SNAPSHOT
      console.log("📝 Writing to root document...");
      await setDoc(
        rootRef,
        {
          ...data,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );
      console.log("✅ Root upsert success");

      console.log("🔥 UPSERT COMPLETE");
    } catch (error: any) {
      console.error("❌ UPSERT FAILED:", error);
      return rejectWithValue(error.message);
    }
  }
);



export const markAllNotificationsAsRead = createAsyncThunk<
  void,
  string
>("notifications/markAllAsRead", async (userId) => {
  const q = query(
    collection(db, "notifications", userId, "messages"),
    orderBy("createdAt", "desc")
  );

  const snap = await getDocs(q);

  const updates = snap.docs.map((docSnap) =>
    updateDoc(docSnap.ref, { read: true })
  );

  await Promise.all(updates);
});


/* ─────────────────────────────
   STOP REALTIME LISTENER (CRITICAL)
───────────────────────────── */

export const stopUserNotificationsListener =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const unsub = getState().notifications.unsubscribe;
    if (unsub) {
      unsub();
      dispatch(setUnsubscribe(undefined));
    }
  };

/* ─────────────────────────────
   SLICE
───────────────────────────── */

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.notifications = action.payload;
    },
    setUnsubscribe: (
      state,
      action: PayloadAction<(() => void) | undefined>
    ) => {
      state.unsubscribe = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserNotifications.fulfilled, (state, action) => {
      state.notifications = action.payload;
    });

    builder.addCase(markNotificationAsRead.fulfilled, (state, action) => {
      const idx = state.notifications.findIndex(
        (n) => n.notificationId === action.payload
      );
      if (idx !== -1) {
        state.notifications[idx].read = true;
      }
    });

    builder.addCase(markAllNotificationsAsRead.fulfilled, (state) => {
      state.notifications.forEach((n) => (n.read = true));
    });
  },
});

export const {
  setNotifications,
  setUnsubscribe,
  setError,
  clearNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
